import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/AuthContext";
import { AppProvider } from "@/lib/AppContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";
import { PublicLayout } from "@/components/PublicLayout";

// Public pages
import Landing from "@/pages/Landing";
import Blog from "@/pages/Blog";
import BlogPostPage from "@/pages/BlogPost";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PublicBooking from "@/pages/PublicBooking";
import PublicConcierge from "@/pages/PublicConcierge";
import MockPay from "@/pages/MockPay";

// Admin pages
import Dashboard from "@/pages/Dashboard";
import Rooms from "@/pages/Rooms";
import Reservations from "@/pages/Reservations";
import Booking from "@/pages/Booking";
import Guests from "@/pages/Guests";
import Concierge from "@/pages/Concierge";
import Housekeeping from "@/pages/Housekeeping";
import Maintenance from "@/pages/Maintenance";
import Reviews from "@/pages/Reviews";
import Copilot from "@/pages/Copilot";
import Energy from "@/pages/Energy";
import FoodWaste from "@/pages/FoodWaste";
import Integrations from "@/pages/Integrations";
import Migration from "@/pages/Migration";
import Strategy from "@/pages/Strategy";
import Loop from "@/pages/Loop";
import AuditLog from "@/pages/AuditLog";
import BackupPage from "@/pages/Backup";
import BlogAdmin from "@/pages/BlogAdmin";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import AIProviders from "@/pages/AIProviders";
import FloorMap from "@/pages/FloorMap";
import SmartDevices from "@/pages/SmartDevices";

const OWNER_MGR = ["owner", "manager"];
const FRONTDESK_PLUS = ["owner", "manager", "front_desk"];
const HK_PLUS = ["owner", "manager", "housekeeping"];
const MAINT_PLUS = ["owner", "manager", "maintenance"];
const PUBLIC_LOGIN_ONLY = process.env.REACT_APP_PUBLIC_LOGIN_ONLY === "true";

const publicPage = (page) =>
    PUBLIC_LOGIN_ONLY ? <Navigate to="/login" replace /> : page;

function Admin({ roles, children }) {
    return (
        <ProtectedRoute roles={roles}>
            <AppShell>{children}</AppShell>
        </ProtectedRoute>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <AppProvider>
                <BrowserRouter>
                    <Routes>
                        {/* Public */}
                        <Route element={<PublicLayout />}>
                            <Route path="/" element={<Landing />} />
                            <Route path="/blog" element={publicPage(<Blog />)} />
                            <Route path="/blog/:slug" element={publicPage(<BlogPostPage />)} />
                            <Route path="/book" element={publicPage(<PublicBooking />)} />
                            <Route path="/chat" element={publicPage(<PublicConcierge />)} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={publicPage(<Register />)} />
                        </Route>

                        {/* Standalone (mock) payment checkout page */}
                        <Route path="/mock-pay" element={publicPage(<MockPay />)} />

                        {/* Admin */}
                        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                        <Route path="/admin/dashboard" element={<Admin><Dashboard /></Admin>} />
                        <Route path="/admin/rooms" element={<Admin><Rooms /></Admin>} />
                        <Route path="/admin/reservations" element={<Admin><Reservations /></Admin>} />
                        <Route path="/admin/booking" element={<Admin><Booking /></Admin>} />
                        <Route path="/admin/guests" element={<Admin roles={FRONTDESK_PLUS}><Guests /></Admin>} />
                        <Route path="/admin/concierge" element={<Admin><Concierge /></Admin>} />
                        <Route path="/admin/housekeeping" element={<Admin roles={HK_PLUS}><Housekeeping /></Admin>} />
                        <Route path="/admin/maintenance" element={<Admin roles={MAINT_PLUS}><Maintenance /></Admin>} />
                        <Route path="/admin/reviews" element={<Admin roles={FRONTDESK_PLUS}><Reviews /></Admin>} />
                        <Route path="/admin/copilot" element={<Admin><Copilot /></Admin>} />
                        <Route path="/admin/energy" element={<Admin><Energy /></Admin>} />
                        <Route path="/admin/food-waste" element={<Admin><FoodWaste /></Admin>} />
                        <Route path="/admin/integrations" element={<Admin roles={OWNER_MGR}><Integrations /></Admin>} />
                        <Route path="/admin/migration" element={<Admin roles={OWNER_MGR}><Migration /></Admin>} />
                        <Route path="/admin/strategy" element={<Admin><Strategy /></Admin>} />
                        <Route path="/admin/loop" element={<Admin roles={OWNER_MGR}><Loop /></Admin>} />
                        <Route path="/admin/ai-providers" element={<Admin roles={OWNER_MGR}><AIProviders /></Admin>} />
                        <Route path="/admin/floor-map" element={<Admin roles={OWNER_MGR}><FloorMap /></Admin>} />
                        <Route path="/admin/devices" element={<Admin roles={OWNER_MGR}><SmartDevices /></Admin>} />
                        <Route path="/admin/audit" element={<Admin roles={OWNER_MGR}><AuditLog /></Admin>} />
                        <Route path="/admin/backup" element={<Admin roles={OWNER_MGR}><BackupPage /></Admin>} />
                        <Route path="/admin/blog" element={<Admin roles={OWNER_MGR}><BlogAdmin /></Admin>} />
                        <Route path="/admin/users" element={<Admin roles={OWNER_MGR}><Users /></Admin>} />
                        <Route path="/admin/settings" element={<Admin><Settings /></Admin>} />

                        <Route path="*" element={<Navigate to={PUBLIC_LOGIN_ONLY ? "/login" : "/"} replace />} />
                    </Routes>
                    <Toaster richColors position="top-right" />
                </BrowserRouter>
            </AppProvider>
        </AuthProvider>
    );
}
